// Debug levels
export const DEBUG_LEVELS = {
    NONE: 0,
    ERROR: 1,
    WARN: 2,
    INFO: 3,
    DEBUG: 4,
    TRACE: 5
};

class DebugLogger {
    constructor() {
        // Check if we're in a browser environment
        const isBrowser = typeof window !== 'undefined';

        if (isBrowser) {
            // Vite/browser environment
            this.enabled = import.meta.env.MODE === 'development';
            this.level = import.meta.env.VITE_DEBUG_LEVEL ? 
                parseInt(import.meta.env.VITE_DEBUG_LEVEL, 10) : 
                DEBUG_LEVELS.NONE;
            this.categories = new Set(import.meta.env.VITE_DEBUG_CATEGORIES ? 
                import.meta.env.VITE_DEBUG_CATEGORIES.split(',') : 
                []);
        } else {
            // Node.js environment
            this.enabled = process.env.NODE_ENV === 'development';
            this.level = process.env.DEBUG_LEVEL ? 
                parseInt(process.env.DEBUG_LEVEL, 10) : 
                DEBUG_LEVELS.NONE;
            this.categories = new Set(process.env.DEBUG_CATEGORIES ? 
                process.env.DEBUG_CATEGORIES.split(',') : 
                []);
        }
    }

    setEnabled(enabled) {
        this.enabled = enabled;
    }

    setLevel(level) {
        this.level = level;
    }

    addCategory(category) {
        this.categories.add(category);
    }

    removeCategory(category) {
        this.categories.delete(category);
    }

    clearCategories() {
        this.categories.clear();
    }

    _shouldLog(level, category) {
        return this.enabled && 
               level <= this.level && 
               (this.categories.size === 0 || this.categories.has(category));
    }

    _formatMessage(category, message, data) {
        const timestamp = new Date().toISOString();
        const dataString = data ? JSON.stringify(data, null, 2) : '';
        return `[${timestamp}] [${category}] ${message} ${dataString}`.trim();
    }

    error(category, message, data) {
        if (this._shouldLog(DEBUG_LEVELS.ERROR, category)) {
            console.error(this._formatMessage(category, message, data));
        }
    }

    warn(category, message, data) {
        if (this._shouldLog(DEBUG_LEVELS.WARN, category)) {
            console.warn(this._formatMessage(category, message, data));
        }
    }

    info(category, message, data) {
        if (this._shouldLog(DEBUG_LEVELS.INFO, category)) {
            console.info(this._formatMessage(category, message, data));
        }
    }

    debug(category, message, data) {
        if (this._shouldLog(DEBUG_LEVELS.DEBUG, category)) {
            console.debug(this._formatMessage(category, message, data));
        }
    }

    trace(category, message, data) {
        if (this._shouldLog(DEBUG_LEVELS.TRACE, category)) {
            console.trace(this._formatMessage(category, message, data));
        }
    }

    // Group related logs
    group(category, label) {
        if (this._shouldLog(this.level, category)) {
            console.group(`[${category}] ${label}`);
        }
    }

    groupEnd(category) {
        if (this._shouldLog(this.level, category)) {
            console.groupEnd();
        }
    }

    // Performance measurement
    time(category, label) {
        if (this._shouldLog(this.level, category)) {
            console.time(`[${category}] ${label}`);
        }
    }

    timeEnd(category, label) {
        if (this._shouldLog(this.level, category)) {
            console.timeEnd(`[${category}] ${label}`);
        }
    }
}

export const debugLogger = new DebugLogger();

// Export a default instance
export default debugLogger; 